import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { MoneyInput } from "@/components/ui/money-input";
import { PriceWithAdjustment } from "@/components/proposals/PriceWithAdjustment";

interface InsuranceStepProps {
  data: any;
  onChange: (data: any) => void;
  readOnly?: boolean;
}

const InsuranceStep = ({ data, onChange, readOnly }: InsuranceStepProps) => {
  const isReadOnly = !!readOnly;
  const [insurance, setInsurance] = useState<any[]>(data.insurance || []);

  const addInsurance = () => {
    if (isReadOnly) return;
    const newInsurance = [{ insurance_title: "", insurance_price: 0, insurance_description: "" }, ...insurance];
    setInsurance(newInsurance);
    onChange({ ...data, insurance: newInsurance });
  };

  const removeInsurance = (index: number) => {
    if (isReadOnly) return;
    const newInsurance = insurance.filter((_, i) => i !== index);
    setInsurance(newInsurance);
    onChange({ ...data, insurance: newInsurance });
  };

  const updateInsurance = (index: number, field: string, value: any) => {
    if (isReadOnly) return;
    const newInsurance = [...insurance];
    newInsurance[index] = { ...newInsurance[index], [field]: value };
    setInsurance(newInsurance);
    onChange({ ...data, insurance: newInsurance });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Insurance</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Add insurance options for the trip
          </p>
        </div>
        <Button type="button" size="sm" onClick={addInsurance} disabled={isReadOnly}>
          <Plus className="mr-2 h-4 w-4" />
          Add Insurance
        </Button>
      </div>

      {insurance.length === 0 ? (
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center ${isReadOnly ? "" : "cursor-pointer transition-colors hover:bg-muted/30"}`}
          onClick={isReadOnly ? undefined : addInsurance}
          onKeyDown={isReadOnly ? undefined : (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              addInsurance();
            }
          }}
          role={isReadOnly ? undefined : "button"}
          tabIndex={isReadOnly ? -1 : 0}
          aria-label={isReadOnly ? undefined : "Add insurance"}
        >
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-full bg-muted p-3">
              <Plus className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">No insurance added yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click "Add Insurance" to get started
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {insurance.map((ins, index) => (
            <div key={index} className="grid gap-5 p-6 border rounded-lg bg-card relative shadow-sm hover:shadow-md transition-shadow">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-3 right-3 h-8 w-8 p-0"
                onClick={() => removeInsurance(index)}
                disabled={isReadOnly}
              >
                <X className="h-4 w-4" />
              </Button>

              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Insurance Title</Label>
                  <Input
                    value={ins.insurance_title}
                    onChange={(e) => updateInsurance(index, "insurance_title", e.target.value)}
                    placeholder="Enter insurance title"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="space-y-2 justify-self-start">
                  <Label className="text-sm font-medium">Insurance Price</Label>
                  <MoneyInput
                    value={ins.insurance_price}
                    onChange={(value) => {
                      updateInsurance(index, "insurance_price", value ? parseFloat(value) : 0);
                    }}
                    placeholder="0.00"
                    maxIntegerDigits={9}
                    className="w-[140px]"
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="border-t pt-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Insurance Description</Label>
                  <Textarea
                    value={ins.insurance_description}
                    onChange={(e) => updateInsurance(index, "insurance_description", e.target.value)}
                    placeholder="Enter detailed description..."
                    className="min-h-[100px]"
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="flex justify-end border-t pt-3">
                <div className="text-right">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Item total</p>
                  <PriceWithAdjustment
                    baseAmount={Number(ins.insurance_price || 0)}
                    proposalData={data}
                    size="md"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InsuranceStep;
